import React, { useState, useEffect } from 'react';
import withAuth from '../../HOC/withAuth';
import { useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import dayjs from 'dayjs';
import { getTitle } from './fun';
import { isNumber } from '../../util';
import { userStore } from '../../stores/UserStore';
import http from '../../http';
import C from '../../colors';
import OrderPreviewPanel from '../../components/OrderPreviewPanel';
import {
  OrderFormColumns,
  OrderFormGrid,
  OrderFormSection,
  OrderFormRow,
  OrderToggle,
  OrderTextInput,
  OrderNumberInput,
} from '../../components/OrderFormControls';
import { orderCreationNextStep } from '../../orderStatus';

function BackIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M19 12H5M11 18l-6-6 6-6"/>
    </svg>
  );
}

const CreateOrder = inject('snackbarStore', 'packageStore')(observer(({ snackbarStore, packageStore }) => {
  const navigate = useNavigate();

  const [packageId, setPackageId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shootLocation, setShootLocation] = useState('');
  const [remark, setRemark] = useState('');
  const [shootTime, setShootTime] = useState(dayjs());
  const [photoCount, setPhotoCount] = useState(0);
  const [photoCountError, setPhotoCountError] = useState('');
  const [forceLimit, setForceLimit] = useState(true);
  const [pricePerPic, setPricePerPic] = useState(0);
  const [pricePerPicError, setPricePerPicError] = useState('');
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);

  useEffect(() => {
    packageStore.fetchPackages();
  }, []);

  const validatePhotoCount = (v) => {
    if (!v) return '可选照片数量不能为空';
    if (!isNumber(v)) return '必须为数字';
    if (v > 1000) return '不能超过 1000';
    return '';
  };
  const validatePricePerPic = (v) => (!isNumber(v) ? '加片价格必须为数字' : '');

  const handlePackageChange = (e) => {
    const val = e.target.value;
    setPackageId(val);
    const pkg = packageStore.packages.find(p => String(p.id) === val);
    if (pkg) setPhotoCount(pkg.photoCount);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const ce = !customerName ? '客户姓名不能为空' : '';
    const fe = validatePhotoCount(photoCount);
    const ppe = !forceLimit ? validatePricePerPic(pricePerPic) : '';
    setPhotoCountError(fe);
    setPricePerPicError(ppe);
    if (ce || fe || ppe) {
      if (ce) snackbarStore.showSnackbar(ce, 'error');
      return;
    }
    const title = getTitle('zh', customerName, shootLocation, shootTime);
    http.post('/user/order', {
      packageId: packageId || null, customerName, customerPhone, shootLocation,
      remark, shootTime, photoCount, title, pricePerPic, isOnlinePayment, forceLimit,
    }).then(res => {
      const order = res.data.order;
      const nextStep = orderCreationNextStep(order);
      navigate(`/user/orders/${order.id}/${nextStep.id}`, {
        state: nextStep.id === 'step2' ? { watermarkReview: true } : undefined,
      });
    }).catch(() => snackbarStore.showSnackbar('创建失败，请重试', 'error'));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '9px 36px', background: C.paper, borderBottom: `1px solid ${C.hair}`, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(700px 180px at 100% -50%, ${C.brand}24, transparent 70%)` }}/>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 13 }}>
          <button onClick={() => navigate('/user/orders')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px 5px 6px',
            background: 'none', border: `1px solid ${C.hair}`,
            borderRadius: 7, cursor: 'pointer',
            fontFamily: C.sans, fontSize: 12, color: C.ink3,
          }}>
            <BackIcon/> 订单列表
          </button>
          <div style={{ width: 1, height: 22, background: C.hair }}/>
          <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 25, lineHeight: 1, fontWeight: 400, color: C.ink, letterSpacing: -0.4 }}>
            创建订单
          </h2>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <form id="create-form" onSubmit={handleSubmit}>
          <OrderFormColumns preview={
            <OrderPreviewPanel showImageName={false} forceLimit={forceLimit} isOnlinePayment={isOnlinePayment} price={pricePerPic} photoCount={photoCount} hasPackage={Boolean(packageId)}/>
          }>
              <OrderFormSection>基本信息</OrderFormSection>

              <OrderFormGrid template={packageStore.packages.length > 0 ? 'minmax(0, 1fr) minmax(180px, .55fr)' : '1fr'}>
                {packageStore.packages.length > 0 && (
                  <OrderFormRow label="套餐" optional>
                    <select data-testid="order-package-select" name="packageId" value={packageId} onChange={handlePackageChange} style={{
                      width: '100%', padding: '9px 12px', borderRadius: 6,
                      border: `1px solid ${C.hair2}`, background: '#fff',
                      fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none',
                    }}>
                      <option value="">— 不使用套餐 —</option>
                      {packageStore.packages.map(p => (
                        <option key={p.id} value={String(p.id)}>{p.name} · {p.photoCount} 张</option>
                      ))}
                    </select>
                  </OrderFormRow>
                )}

                <OrderFormRow label="拍摄时间" optional>
                  <input type="date" value={shootTime?.format?.('YYYY-MM-DD') || shootTime || ''}
                    onChange={e => setShootTime(dayjs(e.target.value))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.hair2}`, fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none', background: '#fff' }}/>
                </OrderFormRow>
              </OrderFormGrid>

              <OrderFormGrid>
                <OrderFormRow label="客户姓名" required>
                  <OrderTextInput name="customerName" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="客户姓名"/>
                </OrderFormRow>

                <OrderFormRow label="客户电话" optional>
                  <OrderTextInput name="customerPhone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="客户电话"/>
                </OrderFormRow>
              </OrderFormGrid>

              <OrderFormSection>选片设置</OrderFormSection>

              <OrderFormGrid>
                <OrderFormRow label="可选照片数量" caption="客户最多可以勾选的照片数量。" required>
                  <OrderNumberInput name="photoCount" value={photoCount} onChange={e => { setPhotoCount(e.target.value); setPhotoCountError(validatePhotoCount(e.target.value)); }}
                    suffix="张" error={!!photoCountError} helperText={photoCountError}/>
                </OrderFormRow>

                <OrderFormRow label="允许加片" caption="开启后允许客户超选并按加片价格计费。" inline last={forceLimit}>
                  <OrderToggle testId="create-allow-extra-toggle" label="允许加片" value={!forceLimit} onChange={v => setForceLimit(!v)}/>
                </OrderFormRow>
              </OrderFormGrid>

              {!forceLimit && (
                <OrderFormGrid>
                  <OrderFormRow label="加片价格" caption="客户超出上限的每张照片将按此价格计费。">
                    <OrderNumberInput name="pricePerPic" value={pricePerPic} onChange={e => { setPricePerPic(e.target.value); setPricePerPicError(validatePricePerPic(e.target.value)); }}
                      prefix="¥" suffix="/张" error={!!pricePerPicError} helperText={pricePerPicError}/>
                  </OrderFormRow>

                  <OrderFormRow label="平台代收款" caption="如需平台代收款，请先绑定微信账号。" inline last>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <OrderToggle testId="create-online-payment-toggle" label="平台代收款" value={isOnlinePayment} disabled={!userStore.wxOpenId} onChange={v => setIsOnlinePayment(v)}/>
                      {!userStore.wxOpenId && (
                        <button data-testid="create-wechat-bind" type="button" onClick={() => userStore.setWechatDialogOpen(true)} style={{
                          padding: '8px 14px', borderRadius: 6, background: C.ink, color: '#fff',
                          border: 'none', fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        }}>绑定微信</button>
                      )}
                    </div>
                  </OrderFormRow>
                </OrderFormGrid>
              )}

              <OrderFormSection>补充信息</OrderFormSection>

              <OrderFormGrid>
                <OrderFormRow label="拍摄地点" optional last>
                  <OrderTextInput name="shootLocation" value={shootLocation} onChange={e => setShootLocation(e.target.value)} placeholder="例如：上海外滩" multiline rows={2}/>
                </OrderFormRow>

                <OrderFormRow label="备注" optional last>
                  <OrderTextInput name="remark" value={remark} onChange={e => setRemark(e.target.value)} placeholder="订单补充说明" multiline rows={2}/>
                </OrderFormRow>
              </OrderFormGrid>
          </OrderFormColumns>
        </form>
      </div>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${C.hair}`,
        padding: '10px 36px',
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        background: C.paper, flexShrink: 0,
      }}>
        <button form="create-form" type="submit" style={{
          fontFamily: C.sans, fontSize: 14, fontWeight: 500,
          padding: '10px 24px', borderRadius: 7,
          background: C.brand, color: '#fff', border: 'none',
          cursor: 'pointer',
          boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
        }}>
          创建订单
        </button>
      </footer>
    </div>
  );
}));

export default withAuth(CreateOrder);
